/**
 * Gating for `usePrototyperWorkbenchFacade.runAutoPrototypeSnapshot`.
 *
 * Kept dependency-free so it stays unit-testable in the node test env.
 */

export type AutoSnapshotReason = 'files' | 'build-start' | 'build' | 'stream-end' | 'resume-build';

/**
 * A turn's pre-build and post-build snapshots have byte-identical content — `executeBuild`
 * writes only into the container, never into `workbenchStore.files`/`assets`. Keying the
 * content-fingerprint dedup by turn alone would therefore always suppress the verified
 * upload that follows the unverified one.
 */
export function snapshotDedupKey(llmInteractionId: string, buildVerified: boolean): string {
  return `${llmInteractionId}:${buildVerified ? 'v1' : 'v0'}`;
}

/**
 * Records the content of a turn's newest snapshot for the upload dedupe. The dedupe skips an
 * upload whose content matches the last one of its kind (verified or not); that is only safe
 * while that one is also the turn's newest. Once a snapshot of the other kind lands, the older
 * entry no longer describes what a restore would load, so it is dropped.
 *
 * Seen with "Fix with AI" (test finding 5): a hand edit saved an unverified snapshot with the
 * crash, the repair put the files back exactly as the version's earlier verified snapshot, and
 * the verified upload was skipped as a duplicate. The newest snapshot, the one a reload loads,
 * kept the crash.
 */
export function recordNewestSnapshotFingerprint(
  fingerprints: Map<string, string>,
  llmInteractionId: string,
  buildVerified: boolean,
  fingerprint: string
): void {
  fingerprints.set(snapshotDedupKey(llmInteractionId, buildVerified), fingerprint);
  fingerprints.delete(snapshotDedupKey(llmInteractionId, !buildVerified));
}

/**
 * Whether an upload would store what the turn's newest snapshot already holds.
 *
 * A verified upload is compared with the newest verified one only (see above). An unverified
 * one is also compared with a newest verified one: the same files saved again unverified add
 * nothing, and only turn the version's newest snapshot into an unverified copy. Every reload
 * did that (production, 24-25 Oct 2026): the restore recorded the version's verified
 * fingerprint, the file autosave looked only for an unverified one, and uploaded the restored
 * files again as a new revision.
 */
export function isSameAsNewestSnapshot(
  fingerprints: ReadonlyMap<string, string>,
  llmInteractionId: string,
  buildVerified: boolean,
  fingerprint: string
): boolean {
  if (fingerprints.get(snapshotDedupKey(llmInteractionId, buildVerified)) === fingerprint) {
    return true;
  }
  return !buildVerified && fingerprints.get(snapshotDedupKey(llmInteractionId, true)) === fingerprint;
}

/** Build-phase snapshots run while the stream is still open, so they bypass the busy/cooldown gates. */
export function isForcedSnapshotReason(reason: AutoSnapshotReason): boolean {
  return reason !== 'files';
}

export function shouldDeferForBusy(args: {
  force: boolean;
  isStreaming: boolean;
  clientToolsBusy: boolean;
}): boolean {
  if (args.force) return false;
  return args.isStreaming || args.clientToolsBusy;
}

export function shouldSkipForCooldown(args: {
  reason: AutoSnapshotReason;
  force: boolean;
  sinceFileMs: number;
  sinceBuildStreamMs: number;
  fileCooldownMs: number;
  buildStreamCooldownMs: number;
}): boolean {
  if (args.force) return false;
  if (args.reason === 'files') return args.sinceFileMs < args.fileCooldownMs;
  return args.sinceBuildStreamMs < args.buildStreamCooldownMs;
}
